import type { ApolloClient } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

/**
 * The problems a reader has dismissed, kept per reader per document so they stay dismissed:
 * Harper's own list lives in the worker, which is released whenever spellcheck goes off. Stored as
 * the text Harper exports, and handed back to it untouched, because its hashes are 64-bit and
 * anything that reads them as numbers rounds them.
 */
export interface IgnoredLintsStore {
  /** Harper's exported list, or null when the reader has dismissed nothing here. */
  load(documentId: UUID): Promise<string | null>;
  /** Replaces the stored list with Harper's current one. */
  save(documentId: UUID, ignoredLints: string): Promise<void>;
}

export function createDocumentIgnoredLintsStore(client: ApolloClient): IgnoredLintsStore {
  return {
    async load(documentId) {
      const { data } = await client.query<
        Apollo.GetDocumentIgnoredSpellcheckLintsQuery,
        Apollo.GetDocumentIgnoredSpellcheckLintsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetDocumentIgnoredSpellcheckLintsDocument,
        variables: { documentId },
      });
      return data?.document.requesterIgnoredSpellcheckLints ?? null;
    },
    async save(documentId, ignoredLints) {
      await client.mutate<
        Apollo.SetDocumentIgnoredSpellcheckLintsMutation,
        Apollo.SetDocumentIgnoredSpellcheckLintsMutationVariables
      >({
        mutation: Apollo.SetDocumentIgnoredSpellcheckLintsDocument,
        variables: { input: { documentId, ignoredLints } },
      });
    },
  };
}
